import {useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {makeStyles} from "@mui/styles";
import {Dialog} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {useAuth} from "../../../contexts/AuthContext";
import {chipTones, trackColours as c} from "./trackStyles";
import {canWithdraw, formatDate, lastUpdate, nextStep, stageSummary, statusChip, timeline} from "./trackModel";
import {fileNameFromLink, parseFileLinks} from "./FileLinks";

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: c.card,
        borderRadius: "16px",
        border: `1px solid ${c.border}`,
        boxShadow: "0 1px 3px rgba(20, 40, 31, 0.05)",
        padding: "24px",
        color: c.text,
    },
    head: {
        display: "grid",
        gridTemplateColumns: "56px minmax(0, 1.35fr) minmax(0, 0.95fr) minmax(0, 1.25fr) 150px",
        gap: "20px",
        alignItems: "start",
        "@media (max-width: 1100px)": {
            gridTemplateColumns: "56px minmax(0, 1fr)",
            "& > :nth-child(n+3)": {
                gridColumn: "2 / 3",
            },
        },
    },
    logo: {
        width: "56px",
        height: "56px",
        borderRadius: "10px",
        border: `1px solid ${c.border}`,
        backgroundColor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        color: c.green,
        fontWeight: 700,
        fontSize: "16px",
    },
    logoImage: {
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
    },
    title: {
        fontSize: "19px",
        fontWeight: 700,
        lineHeight: 1.35,
        margin: 0,
    },
    meta: {
        color: c.muted,
        fontSize: "16px",
        lineHeight: 1.6,
        margin: "4px 0 0",
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        borderRadius: "999px",
        padding: "4px 12px",
        fontSize: "14px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        "&::before": {
            content: '""',
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: "currentColor",
        },
    },
    nextLabel: {
        fontSize: "13px",
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: c.muted,
        margin: "0 0 6px",
    },
    nextText: {
        fontSize: "17px",
        lineHeight: 1.5,
        margin: 0,
    },
    action: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        whiteSpace: "nowrap",
        borderRadius: "10px",
        padding: "10px 16px",
        fontSize: "16px",
        fontWeight: 700,
        textDecoration: "none",
        border: `2px solid ${c.green}`,
        color: c.green,
        backgroundColor: "transparent",
        cursor: "pointer",
        fontFamily: "inherit",
        "&:hover": {
            backgroundColor: "rgba(11, 59, 46, 0.06)",
        },
        "&:disabled": {
            opacity: 0.6,
            cursor: "default",
        },
    },
    actionCell: {
        display: "flex",
        justifyContent: "flex-end",
        "@media (max-width: 1100px)": {
            justifyContent: "flex-start",
        },
    },
    actionPrimary: {
        backgroundColor: c.green,
        color: "#FFFFFF",
        "&:hover": {
            backgroundColor: c.greenHover,
        },
    },
    divider: {
        border: "none",
        borderTop: `1px solid ${c.border}`,
        margin: "20px 0 16px",
    },
    detailsToggle: {
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        fontSize: "16px",
        fontWeight: 700,
        color: c.green,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
    },
    detailsBody: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 0.85fr) minmax(0, 1.15fr)",
        gap: "24px",
        marginTop: "20px",
        "@media (max-width: 1100px)": {
            gridTemplateColumns: "minmax(0, 1fr)",
        },
    },
    timeline: {
        listStyle: "none",
        margin: 0,
        padding: 0,
    },
    step: {
        display: "grid",
        gridTemplateColumns: "34px minmax(0, 1fr)",
        columnGap: "14px",
    },
    markerColumn: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    marker: {
        width: "34px",
        height: "34px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        flex: "none",
    },
    markerDone: {
        backgroundColor: c.green,
        color: "#FFFFFF",
    },
    markerCurrent: {
        border: `3px solid ${c.gold}`,
        boxShadow: `0 0 0 4px ${c.goldSoft}`,
        backgroundColor: "#FFFFFF",
        "&::after": {
            content: '""',
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: c.gold,
        },
    },
    markerUpcoming: {
        border: `2px solid ${c.faint}`,
        backgroundColor: "#FFFFFF",
    },
    line: {
        width: "3px",
        flexGrow: 1,
        minHeight: "22px",
        margin: "4px 0",
        borderRadius: "2px",
        backgroundColor: c.faint,
    },
    lineDone: {
        backgroundColor: c.green,
    },
    stepText: {
        paddingBottom: "20px",
    },
    stepLabel: {
        fontSize: "17px",
        fontWeight: 700,
        lineHeight: "34px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    stepLabelUpcoming: {
        color: c.muted,
        fontWeight: 600,
    },
    nowPill: {
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.1em",
        color: c.goldText,
        backgroundColor: c.goldSoft,
        borderRadius: "999px",
        padding: "2px 10px",
        lineHeight: 1.6,
    },
    stepDetail: {
        color: c.muted,
        fontSize: "15px",
        lineHeight: 1.4,
    },
    stepDetailCurrent: {
        color: c.goldText,
        fontWeight: 600,
    },
    summary: {
        color: c.muted,
        fontSize: "16px",
        lineHeight: 1.65,
        margin: "6px 0 0",
    },
    infoColumn: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    section: {
        borderTop: `1px solid ${c.border}`,
        paddingTop: "16px",
    },
    sectionTitle: {
        fontSize: "15px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: c.muted,
        margin: "0 0 10px",
    },
    interviewTime: {
        fontSize: "19px",
        fontWeight: 700,
        color: c.green,
        margin: "0 0 6px",
    },
    infoText: {
        fontSize: "16px",
        lineHeight: 1.6,
        margin: 0,
    },
    infoLabel: {
        fontWeight: 700,
    },
    messageBox: {
        backgroundColor: c.cream,
        borderRadius: "10px",
        padding: "14px 16px",
        fontSize: "16px",
        lineHeight: 1.6,
        whiteSpace: "pre-wrap",
        margin: 0,
    },
    small: {
        fontSize: "14px",
        color: c.muted,
        margin: "8px 0 0",
    },
    fields: {
        display: "grid",
        gridTemplateColumns: "max-content minmax(0, 1fr)",
        columnGap: "18px",
        rowGap: "10px",
        margin: 0,
        fontSize: "16px",
        lineHeight: 1.5,
        "& dt": {
            fontWeight: 700,
        },
        "& dd": {
            margin: 0,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
        },
        "@media (max-width: 600px)": {
            gridTemplateColumns: "minmax(0, 1fr)",
            "& dd": {
                marginBottom: "6px",
            },
        },
    },
    fileLinks: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
    },
    fileLink: {
        display: "inline-block",
        color: c.green,
        fontWeight: 600,
        border: `1px solid ${c.border}`,
        borderRadius: "8px",
        padding: "5px 10px",
        textDecoration: "none",
        overflowWrap: "anywhere",
        "&:hover": {
            backgroundColor: c.cream,
        },
    },
    buttonRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        marginTop: "14px",
    },
    textButton: {
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "16px",
        fontWeight: 700,
        color: c.green,
        textDecoration: "underline",
    },
    withdrawButton: {
        background: "none",
        border: `2px solid ${chipTones.red.color}`,
        color: chipTones.red.color,
        borderRadius: "10px",
        padding: "8px 14px",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "15px",
        fontWeight: 700,
        "&:hover": {
            backgroundColor: chipTones.red.background,
        },
        "&:disabled": {
            opacity: 0.6,
            cursor: "default",
        },
    },
    withdrawConfirm: {
        backgroundColor: chipTones.red.color,
        color: "#FFFFFF",
        "&:hover": {
            backgroundColor: "#7A231B",
        },
    },
    error: {
        color: chipTones.red.color,
        fontSize: "15px",
        margin: "8px 0 0",
    },
    dialog: {
        padding: "26px",
        fontFamily: "Montserrat, sans-serif",
        color: c.text,
    },
    dialogTitle: {
        fontSize: "21px",
        fontWeight: 700,
        color: c.green,
        margin: "0 0 10px",
    },
}));

function companyInitials(name) {
    return (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");
}

function ActionButton({action, classes, onOpenDetails}) {
    const className = `${classes.action} ${action.primary ? classes.actionPrimary : ""}`;
    if (action.href) {
        return (
            <a className={className} href={action.href} target="_blank" rel="noopener noreferrer">
                {action.label} <span aria-hidden="true">↗</span>
            </a>
        );
    }
    if (action.opensDetails) {
        return <button className={className} onClick={onOpenDetails}>{action.label}</button>;
    }
    return <Link className={className} to={action.to}>{action.label}</Link>;
}

// application: the record from list-applications. Opening Details loads the full record from view-application.
// onUpdate(applicationID, fields) and onWithdrawn(applicationID) tell the page about changes.
const TrackApplicationCard = ({application, logo, defaultOpen = false, highlight = false, onUpdate, onWithdrawn}) => {
    const classes = useStyles();
    const {viewApplication, acceptInterview, withdrawApplication} = useAuth();
    const cardRef = useRef(null);
    const detailsRef = useRef(null);

    const [open, setOpen] = useState(defaultOpen || highlight);
    const [details, setDetails] = useState(null);
    const [detailsState, setDetailsState] = useState("idle");
    const [accepting, setAccepting] = useState(false);
    const [actionError, setActionError] = useState("");
    const [confirmWithdraw, setConfirmWithdraw] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);
    const [withdrawError, setWithdrawError] = useState("");

    const applicationID = application["application-id"];
    // Full details, once loaded, add to (and take priority over) the list record.
    const app = details ? {...application, ...details.application} : application;

    function loadDetails() {
        setDetailsState("loading");
        viewApplication(applicationID).then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                const profileCvLink = data.data["profile_data"] ? data.data["profile_data"]["cvLink"] : "";
                setDetails({
                    application: data.data["application_data"] || {},
                    position: data.data["position_data"] || {},
                    // Prefer the CV recorded with the application; fall back to the profile's current CV.
                    cvLink: data.data["cvLink"] || profileCvLink || ""
                });
                setDetailsState("ready");
            } else {
                setDetailsState("error");
            }
        }).catch((err) => {
            console.warn(err);
            setDetailsState("error");
        });
    }

    useEffect(() => {
        if (open && detailsState === "idle") {
            loadDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        if (highlight && cardRef.current) {
            cardRef.current.scrollIntoView({behavior: "smooth", block: "start"});
        }
    }, [highlight])

    function openDetails() {
        setOpen(true);
        setTimeout(() => detailsRef.current && detailsRef.current.scrollIntoView({behavior: "smooth", block: "nearest"}), 50);
    }

    function accept() {
        if (accepting) {
            return;
        }
        setAccepting(true);
        setActionError("");

        acceptInterview(applicationID).then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                setDetails((current) => current && {...current, application: {...current.application, "interview-accepted": true}});
                onUpdate && onUpdate(applicationID, {"interview-accepted": true});
            } else {
                setActionError(data.data["reason"] || "We were unable to accept the invitation.");
            }
        }).catch((err) => {
            console.warn(err);
            setActionError("We were unable to accept the invitation. Please check your connection and try again.");
        }).finally(() => {
            setAccepting(false);
        });
    }

    function withdraw() {
        if (withdrawing) {
            return;
        }
        setWithdrawing(true);
        setWithdrawError("");

        withdrawApplication(applicationID).then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                setConfirmWithdraw(false);
                onWithdrawn && onWithdrawn(applicationID);
            } else {
                setWithdrawError(data.data["reason"] || "We were unable to withdraw this application.");
            }
        }).catch((err) => {
            console.warn(err);
            setWithdrawError("We were unable to withdraw this application. Please check your connection and try again.");
        }).finally(() => {
            setWithdrawing(false);
        });
    }

    const chip = statusChip(app);
    const step = nextStep(app);
    const updated = lastUpdate(app);
    const steps = timeline(app);
    const detailsID = `details-${applicationID}`;
    const files = parseFileLinks(app["coverFile"]);

    return (
        <article className={classes.card} ref={cardRef}>
            <div className={classes.head}>
                <div className={classes.logo}>
                    {logo
                        ? <img className={classes.logoImage} src={logo} alt={`${app["company-name"]} logo`}/>
                        : <span aria-hidden="true">{companyInitials(app["company-name"])}</span>}
                </div>
                <div>
                    <h2 className={classes.title}>{app["position-name"]}</h2>
                    <p className={classes.meta}>
                        {app["company-name"]}
                        {app["reference"] && <><br/>{app["reference"]}</>}
                    </p>
                </div>
                <div>
                    <span className={classes.chip} style={chipTones[chip.tone]}>{chip.label}</span>
                    {updated !== "" && <p className={classes.meta}>{updated}</p>}
                </div>
                <div>
                    <p className={classes.nextLabel}>Next step</p>
                    <p className={classes.nextText}>{step.text}</p>
                </div>
                <div className={classes.actionCell}>
                    {step.action && <ActionButton action={step.action} classes={classes} onOpenDetails={openDetails}/>}
                </div>
            </div>

            <hr className={classes.divider}/>

            <button className={classes.detailsToggle} onClick={() => setOpen(!open)} aria-expanded={open} aria-controls={detailsID}>
                Details <span aria-hidden="true">{open ? "▴" : "▾"}</span>
            </button>

            {open && (
                <div className={classes.detailsBody} id={detailsID} ref={detailsRef}>
                    <ol className={classes.timeline}>
                        {steps.map((s, index) => {
                            const next = steps[index + 1];
                            return (
                                <li className={classes.step} key={s.key} aria-current={s.state === "current" ? "step" : undefined}>
                                    <div className={classes.markerColumn}>
                                        <span className={`${classes.marker} ${{
                                            done: classes.markerDone,
                                            current: classes.markerCurrent,
                                            upcoming: classes.markerUpcoming
                                        }[s.state]}`}>
                                            {s.state === "done" && <CheckIcon sx={{fontSize: 20}}/>}
                                        </span>
                                        {next && <span className={`${classes.line} ${next.state !== "upcoming" ? classes.lineDone : ""}`}/>}
                                    </div>
                                    <div className={classes.stepText}>
                                        <div className={`${classes.stepLabel} ${s.state === "upcoming" ? classes.stepLabelUpcoming : ""}`}>
                                            {s.label}
                                            {s.state === "current" && <span className={classes.nowPill}>NOW</span>}
                                        </div>
                                        {s.detail && (
                                            <div className={`${classes.stepDetail} ${s.state === "current" ? classes.stepDetailCurrent : ""}`}>
                                                {s.detail}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>

                    <div className={classes.infoColumn}>
                        <p className={classes.summary}>{stageSummary(app)}</p>

                        {detailsState === "loading" && <p className={classes.small}>Loading application details...</p>}
                        {detailsState === "error" && (
                            <div>
                                <p className={classes.error}>We were unable to load the details of this application.</p>
                                <button className={classes.textButton} onClick={loadDetails}>Try again</button>
                            </div>
                        )}

                        {app["interview-time"] && (
                            <section className={classes.section}>
                                <h3 className={classes.sectionTitle}>Interview</h3>
                                <p className={classes.interviewTime}>{app["interview-time"]}</p>
                                {app["interviewer"] && <p className={classes.infoText}><span className={classes.infoLabel}>Interviewer:</span> {app["interviewer"]}</p>}
                                {app["location"] && <p className={classes.infoText}><span className={classes.infoLabel}>Location:</span> {app["location"]}</p>}
                                <p className={classes.small}>
                                    {app["interview-accepted"]
                                        ? "You have accepted this interview. Please contact us if you can no longer attend."
                                        : "Please respond within 24 hours, or you may lose your interview slot."}
                                </p>
                                <div className={classes.buttonRow}>
                                    {!app["interview-accepted"] && (
                                        <button className={`${classes.action} ${classes.actionPrimary}`} onClick={accept} disabled={accepting}>
                                            {accepting ? "Accepting..." : "Accept invitation"}
                                        </button>
                                    )}
                                    <a className={classes.action} href="mailto:careers-fair@samaggisamagom.com">Contact us</a>
                                </div>
                                {actionError !== "" && <p className={classes.error}>{actionError}</p>}
                            </section>
                        )}

                        {app["message"] && (
                            <section className={classes.section}>
                                <h3 className={classes.sectionTitle}>Message from {app["company-name"] || "the company"}</h3>
                                <p className={classes.messageBox}>{app["message"]}</p>
                                {app["message-date"] && <p className={classes.small}>Last updated {formatDate(app["message-date"])}</p>}
                            </section>
                        )}

                        {detailsState === "ready" && (
                            <section className={classes.section}>
                                <h3 className={classes.sectionTitle}>Your application</h3>
                                <dl className={classes.fields}>
                                    <dt>Application ID</dt>
                                    <dd>{applicationID}</dd>
                                    <dt>Additional information</dt>
                                    <dd>{app["additional-text"] ? app["additional-text"] : "You did not enter any additional text."}</dd>
                                    {details.cvLink && (
                                        <>
                                            <dt>CV</dt>
                                            <dd>
                                                <a className={classes.fileLink} href={details.cvLink} target="_blank" rel="noopener noreferrer">
                                                    Download the CV the company received
                                                </a>
                                            </dd>
                                        </>
                                    )}
                                    {files.length > 0 && (
                                        <>
                                            <dt>Attached files</dt>
                                            <dd className={classes.fileLinks}>
                                                {files.map((link, index) => (
                                                    <a key={link + index} className={classes.fileLink} href={link} target="_blank" rel="noopener noreferrer">
                                                        {fileNameFromLink(link, index)}
                                                    </a>
                                                ))}
                                            </dd>
                                        </>
                                    )}
                                </dl>
                            </section>
                        )}

                        {canWithdraw(app) && (
                            <section className={classes.section}>
                                <p className={classes.infoText}>Changed your mind? You can withdraw while the company hasn't viewed your application yet.</p>
                                <div className={classes.buttonRow}>
                                    <button className={classes.withdrawButton} onClick={() => setConfirmWithdraw(true)}>Withdraw application</button>
                                </div>
                            </section>
                        )}
                    </div>
                </div>
            )}

            <Dialog open={confirmWithdraw} onClose={() => !withdrawing && setConfirmWithdraw(false)} aria-labelledby={`withdraw-title-${applicationID}`}>
                <div className={classes.dialog}>
                    <h2 className={classes.dialogTitle} id={`withdraw-title-${applicationID}`}>Withdraw this application?</h2>
                    <p className={classes.infoText}>
                        Your application for {app["position-name"]} at {app["company-name"]} will be withdrawn and the company will no longer see it.
                        This can't be undone.
                    </p>
                    {withdrawError !== "" && <p className={classes.error}>{withdrawError}</p>}
                    <div className={classes.buttonRow}>
                        <button className={classes.action} onClick={() => setConfirmWithdraw(false)} disabled={withdrawing}>Keep application</button>
                        <button className={`${classes.withdrawButton} ${classes.withdrawConfirm}`} onClick={withdraw} disabled={withdrawing}>
                            {withdrawing ? "Withdrawing..." : "Withdraw"}
                        </button>
                    </div>
                </div>
            </Dialog>
        </article>
    );
};

export default TrackApplicationCard;
